import { useEffect, useState } from "react";
import "./App.css";
import Header from "./components/header/header";
import Sidebar from "./components/sidebar/sidebar";
import AdminSidebar from "./components/sidebar/AdminSidebar";
import Content from "./components/content/main";
import PageNavigation from "./components/sidebar/pageNavigation";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Footer from "./components/footer/footer";
import Capability from "./components/content/capability";
import Faq from "./components/content/faq";
import Plan from "./components/content/plan";
import Cart from "./components/content/cart";
import Contacts from "./components/content/contacts";
import ScrollToTop from "./components/scroll/scrollToTop";
import { SearchProvider } from "./contexts/SearchContext";
import SearchDialog from "./components/search/SearchDialog";
import ProductPage from "./components/content/product";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AuthDialogProvider } from "./contexts/AuthDialogContext";
import { CartProvider } from "./contexts/CartContext";
import AuthDialog from "./components/auth/AuthDialog";
import AccountInfoPage from './pages/AccountInfoPage';
import AccountOrdersPage from './pages/AccountOrdersPage';
import CheckoutPage from './pages/CheckoutPage';
import HomePage from './pages/HomePage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import RestorePasswordPage from './pages/RestorePasswordPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import AdminPage from './pages/AdminPage';
import AdminMarkupsPage from './pages/AdminMarkupsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserRegistrationPage from './pages/AdminUserRegistrationPage';
import AdminOrdersPage from './pages/AdminOrdersPage';
import SearchByPcodePage from './pages/SearchByPcodePage';
import CatalogWidgetPage from './pages/CatalogWidgetPage';
import LogsPage from './pages/admin/LogsPage';
import AdminWarehousePage from './pages/AdminWarehousePage';
import AdminTyresPage from './pages/AdminTyresPage';
import NotFoundPage from './pages/NotFoundPage';
import GlobalSearchPage from './pages/GlobalSearchPage';
import MultiSearchPage from './pages/MultiSearchPage';
import DeliveryPage from './pages/DeliveryPage';
import ReturnsPage from './pages/ReturnsPage';
import PrivacyPage from './pages/PrivacyPage';
import TyresPage from './pages/TyresPage';
import TyrePage from './pages/TyrePage';

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? "hidden" : "";
  }, [isSidebarOpen]);

  const linksByRoute: Record<string, Array<{ href: string; label: string }>> = {
    "/capability": [
      { href: "#multisearch", label: "Многозадачный поиск" },
      { href: "#search", label: "Интеллектуальный поиск" },
      { href: "#catalog", label: "Каталог" },
      { href: "#logistics", label: "Логистика" },
      { href: "#lk", label: "Личный кабинет" },
      { href: "#compare", label: "Сравнение товаров" },
      { href: "#reviews", label: "Отзывы и рейтинг" },
      { href: "#support", label: "Поддержка" },
    ],
  };

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const PageWithNavigation = () => {
    const location = useLocation();
    const { isAuthenticated, user } = useAuth();
    const links = linksByRoute[location.pathname] || [];
    const closeSidebar = 
    location.pathname === '/login' || 
    location.pathname === '/register' || 
    location.pathname === '/' || 
    location.pathname === '/restore-password' ||
    location.pathname === '/cart'||
    location.pathname === '/checkout' ||
    location.pathname === '/product' ||
    location.pathname === '/search-by-pcode' ||
    location.pathname === '/catalog-widget' ||
    location.pathname === '/multi-search' ||
    location.pathname === '/delivery' ||
    location.pathname === '/returns' ||
    location.pathname === '/privacy' ||
    location.pathname.startsWith('/tyre/');
    
    // Скрываем сайдбары для 404 страницы
    const is404Page = !['/', '/login', '/register', '/restore-password', '/cart', '/checkout', 
      '/product', '/search-by-pcode', '/catalog-widget', '/multi-search', '/delivery', '/returns', '/privacy',
      '/project', '/capability', '/faq', '/plan', '/contacts', '/account', '/account/info', 
      '/account/orders', '/order-success', '/admin', '/admin/orders', '/admin/markups', 
      '/admin/users', '/admin/user-registration', '/admin/logs'].some(route => 
        location.pathname === route || location.pathname.startsWith('/admin/')
      );
    const closeRightSidebar = location.pathname === '/cart' || location.pathname.startsWith('/admin');
    const isAdminPage = location.pathname.startsWith('/admin');

    useEffect(() => {
      if (location.pathname.startsWith('/account') && !isAuthenticated) {
        const token = localStorage.getItem('token');
        if (!token) {
          // Вместо перенаправления на страницу входа, открываем попап
          // navigate('/login');
        }
      }
    }, [location.pathname, isAuthenticated]);

    // Автоматическое перенаправление администраторов на /admin/markups только при первой загрузке приложения
    useEffect(() => {
      // Проверяем, был ли уже выполнен редирект в этой сессии
      const hasRedirected = sessionStorage.getItem('adminRedirected');
      if (isAuthenticated && user?.role === 'Admin' && location.pathname === '/' && !hasRedirected) {
        sessionStorage.setItem('adminRedirected', 'true');
        navigate('/admin/markups', { replace: true });
      }
      // Сбрасываем флаг при выходе из системы
      if (!isAuthenticated) {
        sessionStorage.removeItem('adminRedirected');
      }
    }, [isAuthenticated, user, location.pathname, navigate]);

    return (
      <div className="flex-1 flex flex-col">
        <div className="mx-auto flex w-full max-w-container flex-row gap-4 md:gap-6 xl:gap-12 px-2 md:px-4 flex-1">
          {!closeSidebar && !is404Page && (
            isAdminPage ? (
              <AdminSidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
            ) : (
              <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
            )
          )}
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/project" element={<Content />} />
            <Route path="/capability" element={<Capability />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/restore-password" element={<RestorePasswordPage />} />
            <Route path="/account" element={<AccountInfoPage />} />
            <Route path="/account/info" element={<AccountInfoPage />} />
            <Route path="/account/orders" element={<AccountOrdersPage />} />
            <Route path="/order-success" element={<OrderSuccessPage />} />
            <Route path="/search-by-pcode" element={<SearchByPcodePage />} />
            <Route path="/catalog-widget" element={<CatalogWidgetPage />} />
            <Route path="/global-search" element={<GlobalSearchPage />} />
            <Route path="/multi-search" element={<MultiSearchPage />} />
            <Route path="/delivery" element={<DeliveryPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/tyres" element={<TyresPage />} />
            <Route path="/tyre/:id" element={<TyrePage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/markups"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminMarkupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/user-registration"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminUserRegistrationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <LogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/warehouse"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminWarehousePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tyres"
              element={
                <ProtectedRoute requiredRole="Admin">
                  <AdminTyresPage />
                </ProtectedRoute>
              }
            />
            {/* 404 Route - должен быть последним */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          {!closeSidebar && !closeRightSidebar && !is404Page && <PageNavigation links={links} />}
        </div>
      </div>
    );
  };

  return (
    <AuthProvider>
      <AuthDialogProvider>
        <CartProvider>
          <ScrollToTop />
          <div className="flex min-h-screen flex-col">
            <SearchProvider>
              <Header onMenuClick={toggleSidebar} />
              <SearchDialog />
              <AuthDialog />
            </SearchProvider>
            <main className="flex-1">
              <PageWithNavigation />
            </main>
            <Footer />
          </div>
        </CartProvider>
      </AuthDialogProvider>
    </AuthProvider>
  );
}

export default App;
